import http from '../http'

/**
 * 用户登录
 * @param {Object} data 包含用户名和密码
 * @returns {Promise} 返回登录结果
 */
export function login(data) {
  return http.post('/users/login', data)
}

/**
 * 用户登出
 * @returns {Promise} 返回成功的Promise，不再调用后台API
 */
export function logout() {
  // 清除本地存储的token
  localStorage.removeItem('token')
  localStorage.removeItem('refreshToken')
  
  // 返回一个成功的Promise
  return Promise.resolve({
    data: {
      success: true,
      message: '登出成功'
    }
  })
}

/**
 * 分页查询所有用户信息
 * @param {Object} params 查询参数
 * @param {String} [params.username] - 用户名(可选，支持模糊查询)
 * @param {String} [params.phone] - 联系电话(可选，支持模糊查询)
 * @param {Number} [params.roleId] - 角色ID(可选，精确匹配)
 * @param {Number} [params.unitId] - 单位ID(可选，精确匹配)
 * @param {String} [params.unitName] - 单位名称(可选，模糊匹配)
 * @param {Number} [params.page] - 页码，从0开始，默认0
 * @param {Number} [params.size] - 每页大小，默认10
 * @param {String} [params.sortBy] - 排序字段，默认id
 * @param {String} [params.sortDir] - 排序方向，asc/desc，默认desc
 * @returns {Promise} 返回用户信息列表和分页信息
 */
export function query_users_pagination(params = {}) {
  // 判断是否有搜索条件，如果有则使用搜索接口
  const hasSearchParams = params.username || params.phone || params.roleId || params.unitId || params.unitName;
  const endpoint = hasSearchParams ? '/users/search/page' : '/users/page';
  
  return http.get(endpoint, { params })
}

/**
 * 获取指定ID的用户详细信息
 * @param {Number|String} id - 用户ID
 * @returns {Promise} 返回用户详细信息
 */
export function query_user_by_id(id) {
  return http.get(`/users/${id}`)
}

/**
 * 保存新增或编辑的用户信息
 * @param {Object} data 用户信息数据
 * @param {Number} [data.id] - 用户ID(更新时必填，新增时不填)
 * @param {String} data.username - 用户名(必填)
 * @param {String} [data.password] - 密码(新增时可选，默认为"000000"，更新时可选)
 * @param {String} data.phone - 联系电话(必填)
 * @param {Number} data.roleId - 角色ID(必填)
 * @param {Number} data.unitId - 单位ID(必填)
 * @returns {Promise} 返回保存结果
 */
export function save_user(data) {
  return http.post('/users', data)
}

/**
 * 获取用户详情
 * @param {Number} userId 用户ID
 * @returns {Promise} 返回用户详情
 */
export function getUserInfo(userId) {
  return http.get(`/users/${userId}`)
}

/**
 * 更新用户信息
 * @param {Object} data 更新的用户数据
 * @returns {Promise} 请求结果
 */
export function update_user(data) {
  return http.post('/user/update', data)
}

/**
 * 删除用户
 * @param {Number|String} id - 用户ID
 * @returns {Promise} 返回删除结果
 */
export function del_user(id) {
  return http.delete(`/users/${id}`)
}

/**
 * 重置用户密码
 * @param {Number|String} id - 用户ID
 * @returns {Promise} 返回重置结果
 */
export function reset_user_password(id) {
  return http.post(`/users/${id}/reset-password`)
}

/**
 * 修改用户密码
 * @param {Number|String} id - 用户ID
 * @param {Object} data - 密码数据
 * @param {String} data.oldPassword - 原密码，必填
 * @param {String} data.newPassword - 新密码，必填
 * @returns {Promise} 返回修改结果
 */
export function change_user_password(id, data) {
  return http.post(`/users/${id}/change-password`, data)
}

/**
 * 获取指定类型的单位列表
 * @param {Object} params 查询参数
 * @param {String} params.type - 单位类型("资源组织机构"、"供应商"或"客户")
 * @returns {Promise} 返回单位列表
 */
export function getUnitList(params = {}) {
  // 将type参数映射到unit_type
  const queryParams = {
    ...params,
    unit_type: params.type,
  }
  
  // 删除原始type参数
  delete queryParams.type
  
  return http.get('/unit-collections', { params: queryParams })
}

export default {
  login,
  logout,
  query_users_pagination,
  query_user_by_id,
  save_user,
  del_user,
  reset_user_password,
  change_user_password,
  getUserInfo,
  update_user,
  getUnitList
}
